import React from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { fmtDateTime } from "../lib/labels";

const CONF = { interna: "Interna", proyecto: "Proyecto", confidencial: "Confidencial", publica: "Distribuible" };

export default function ReunionActaPortal() {
  const { mid } = useParams();
  const qc = useQueryClient();
  const { data, error } = useQuery({
    queryKey: ["acta-portal", mid],
    queryFn: async () => (await api.get(`/reuniones/${mid}/acta/portal`)).data,
    retry: false,
  });
  const ack = useMutation({
    mutationFn: async ({ rid, event }) => (await api.post(`/reuniones/${mid}/acta/destinatario/${rid}/evento`, { event })).data,
    onSuccess: () => { toast.success("Acuse registrado"); qc.invalidateQueries({ queryKey: ["acta-portal", mid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });

  if (error) {
    return (
      <div className="p-8" data-testid="portal-denied">
        <p className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Acceso restringido: este portal es solo para destinatarios de la distribución del acta. {apiError(error)}
        </p>
      </div>
    );
  }
  if (!data) return <div className="p-8 text-sm text-slate-500">Cargando…</div>;

  const mine = data.my_recipient;
  const confirmed = (mine?.acknowledgements || []).some((a) => a.kind === "aceptacion");

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6" data-testid="portal-page">
      <Link to="/reuniones" className="flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid="portal-back">
        <ArrowLeft className="h-3 w-3" /> Volver
      </Link>
      <PageHeader title={`Acta distribuida — ${data.meeting.title}`} subtitle={data.note} testid="portal-title"
        actions={<div className="flex gap-2">
          <ToneChip label={data.version.label} tone="success" testid="portal-version" />
          {data.confidentiality && <ToneChip label={CONF[data.confidentiality]} tone="info" testid="portal-conf" />}
        </div>} />
      <p className="text-xs text-slate-500" data-testid="portal-meta">
        Reunión del {fmtDateTime(data.meeting.date)} · aprobada {fmtDateTime(data.version.approved_at)} · observaciones hasta {fmtDateTime(data.observations_deadline)}
      </p>

      {mine && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3" data-testid="portal-ack">
          <p className="text-sm text-blue-900">
            <CheckCircle2 className="mr-1 inline h-4 w-4" />
            Acuse explícito: el silencio no implica aceptación. Estado actual: <strong>{mine.status}</strong>.
          </p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" data-testid="portal-ack-open" disabled={ack.isPending}
              onClick={() => ack.mutate({ rid: mine.id, event: "abierto" })}>Marcar como leída</Button>
            <Button size="sm" data-testid="portal-ack-confirm" disabled={ack.isPending || confirmed}
              onClick={() => ack.mutate({ rid: mine.id, event: "confirmado" })}>
              {confirmed ? "Aceptación registrada" : "Confirmar recepción y aceptación"}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="portal-sections">
        {data.sections.map((s) => (
          <div key={s.key} className={`rounded-md border bg-white p-3 ${s.pending_info ? "border-amber-200" : "border-slate-200"}`} data-testid={`portal-section-${s.key}`}>
            <p className="text-sm font-semibold text-slate-800">{s.title}</p>
            <p className={`mt-1 whitespace-pre-line text-sm ${s.pending_info ? "italic text-amber-700" : "text-slate-700"}`}>{s.content}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400">Si tienes observaciones sobre alguna sección, comunícalas al organizador de la reunión antes del plazo indicado.</p>
    </div>
  );
}
